import { sceneOf } from './scenes/scenes';
import { MODEL_CHOICES } from './scenes/catalog';

export const EMOJI = /\p{Extended_Pictographic}(️|‍\p{Extended_Pictographic})*️?/gu;

export function spokenText(text: string): string {
  return text.replace(EMOJI, '').replace(/\s+/g, ' ').trim();
}

export const MOVERS: Record<string, { emoji: string; word: string }> = {
  cat: { emoji: '🐱', word: 'cat' },
  ball: { emoji: '⚽', word: 'ball' },
  teddy: { emoji: '🧸', word: 'teddy' },
  dog: { emoji: '🐶', word: 'dog' },
  apple: { emoji: '🍎', word: 'apple' },
};

export const BLOCK_COLOURS: Record<string, string> = {
  red: '#e0503f',
  blue: '#2f74c8',
  yellow: '#f4c430',
  green: '#3a9a50',
  orange: '#f08a2a',
  purple: '#8a4fc0',
  white: '#ffffff',
};

export const SHAPES = ['circle', 'square', 'triangle', 'line', 'cross', 'zigzag', 'wave'];

export type TraceGuide = 'trace' | 'faint' | 'copy';

export function isShape(t: string) {
  return SHAPES.includes(t.trim().toLowerCase());
}

export const ODD_ONE_OUT = 'Which one does not belong?';
export const BLOCK_COPY = 'Make one the same';
export const PAIRS_FACE_UP = 'Find the pairs';
export const PAIRS_FACE_DOWN = 'Turn over two cards to find a pair';
export const WAIT_DEFAULT = 'Wait for green';
export const GO = 'Go!';

export const touchPrompt = (verb: string, label: string) => `${verb} the ${label}`;
export const findPrompt = (label: string) => `Find the ${label}`;
export const placePrompt = (mover: string, label: string) => `Put the ${mover} ${label}`;
export const sortPrompt = (word: string) => `Where does the ${word} go?`;
export const labelInstruction = (sceneKind: string | undefined) => (sceneKind === 'dress' ? 'Dress the person' : 'Put the words on the picture');

export function tracePrompt(target: string, guide: TraceGuide) {
  const t = target.trim();
  if (isShape(t)) return guide === 'copy' ? `Draw a ${t.toLowerCase()}` : `Trace the ${t.toLowerCase()}`;
  const what = /^\d+$/.test(t) ? `the number ${t}` : t.length === 1 ? `the letter ${t}` : t;
  return guide === 'copy' ? `Write ${what}` : `Trace ${what}`;
}

const SPOKEN_KEYS = new Set(['say', 'answer', 'label', 'prompt', 'prompts', 'items', 'parts', 'word', 'words', 'text', 'phrase', 'phrases', 'hot', 'cold', 'alternates', 'sentence']);

function collect(node: unknown, spoken: boolean, out: string[]) {
  if (Array.isArray(node)) node.forEach((n) => collect(n, spoken, out));
  else if (node && typeof node === 'object') Object.entries(node).forEach(([k, v]) => collect(v, SPOKEN_KEYS.has(k), out));
  else if (spoken && typeof node === 'string' && node.length <= 200) out.push(node);
}

const asStrings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const rowsOf = (config: Record<string, unknown>) =>
  (Array.isArray(config.sequences) ? config.sequences : []) as { label?: string; items?: string[] }[];

export function programPhrases(kind: string, config: Record<string, unknown>): string[] {
  const out: string[] = [];
  collect(config, false, out);
  const str = (k: string, d: string) => (typeof config[k] === 'string' ? (config[k] as string) : d);
  const wanted = asStrings(config.parts);
  const scene = sceneOf(config.scene);
  const sceneParts = scene ? scene.parts.filter((p) => wanted.includes(p.id)) : [];

  switch (kind) {
    case 'findPart':
      sceneParts.forEach((p) => out.push(touchPrompt('Touch', p.label)));
      break;
    case 'findInScene':
      sceneParts.forEach((p) => out.push(touchPrompt('Find', p.label)));
      break;
    case 'placeIt': {
      const mover = (MOVERS[str('mover', 'cat')] ?? MOVERS.cat).word;
      out.push(mover);
      sceneParts.forEach((p) => out.push(placePrompt(mover, p.label)));
      break;
    }
    case 'rotateFind': {
      const model = MODEL_CHOICES.find((m) => m.id === config.scene);
      (model ? model.parts.filter((p) => wanted.includes(p.id)) : []).forEach((p) => out.push(findPrompt(p.label)));
      break;
    }
    case 'labelParts':
      out.push(labelInstruction(scene?.kind));
      scene?.parts.forEach((p) => out.push(p.label));
      break;
    case 'traceIt': {
      const guide = str('guide', 'trace') as TraceGuide;
      ((config.prompts ?? []) as { prompt?: string }[]).forEach((p) => { if (p?.prompt) out.push(tracePrompt(String(p.prompt), guide)); });
      break;
    }
    case 'waitForGo':
      out.push(WAIT_DEFAULT, GO);
      break;
    case 'sortBins':
      rowsOf(config).forEach((r) => asStrings(r.items).forEach((i) => out.push(spokenText(i), sortPrompt(spokenText(i)))));
      break;
    case 'oddOneOut':
      out.push(ODD_ONE_OUT);
      break;
    case 'memoryPairs':
      out.push(PAIRS_FACE_UP, PAIRS_FACE_DOWN);
      break;
    case 'blockCopy':
      out.push(BLOCK_COPY, ...Object.keys(BLOCK_COLOURS));
      break;
    case 'slotOrder':
    case 'taskChain':
    case 'findAll':
      rowsOf(config).forEach((r) => { if (r.label) out.push(r.label); asStrings(r.items).forEach((i) => out.push(i)); });
      break;
  }
  return [...new Set(out.map(spokenText).filter((t) => /\p{L}/u.test(t)))];
}
